import { useState } from "react";
import { Loader2, Building2, CheckCircle2, XCircle } from "lucide-react";
import {
  useGetMyFirmRefereeRequestsQuery,
  useAcceptFirmRefereeRequestMutation,
  useRejectFirmRefereeRequestMutation,
} from "../api/firmApi";
import { describeApiError } from "../../../lib/apiError";

export default function MyFirmRefereeRequestsPage() {
  const { data: rows = [], isLoading } = useGetMyFirmRefereeRequestsQuery();
  const [accept, { isLoading: accepting }] = useAcceptFirmRefereeRequestMutation();
  const [reject, { isLoading: rejecting }] = useRejectFirmRefereeRequestMutation();
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const busy = accepting || rejecting;

  return (
    <div className="w-full p-4 md:p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Firm Referee Requests</h1>
      <p className="text-sm text-gray-500 mb-6">Firms that have listed you as a referee.</p>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

      {isLoading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed rounded-xl py-16 text-center text-gray-400">No referee requests.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r: any) => (
            <div key={r.id} className="bg-white border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{r.application?.practice_name ?? "Firm"}</p>
                  <p className="text-xs text-gray-400">Applicant: {r.application?.user?.name ?? "—"}</p>
                </div>
                {r.decision === "pending" ? (
                  <span className="text-xs text-amber-600">awaiting your response</span>
                ) : r.decision === "approved" ? (
                  <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> approved</span>
                ) : (
                  <span className="text-xs text-red-600 flex items-center gap-1"><XCircle className="w-4 h-4" /> rejected</span>
                )}
              </div>

              {r.decision === "pending" && rejectFor !== r.id && (
                <div className="flex gap-2 mt-3">
                  <button disabled={busy}
                    onClick={async () => { setError(null); try { await accept({ refereeId: r.id }).unwrap(); } catch (e: any) { setError(describeApiError(e, "Couldn't submit your response.")); } }}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-60">Approve</button>
                  <button disabled={busy} onClick={() => { setRejectFor(r.id); setReason(""); }}
                    className="px-4 py-2 rounded-lg border text-sm text-gray-700 disabled:opacity-60">Reject</button>
                </div>
              )}

              {rejectFor === r.id && (
                <div className="mt-3 space-y-2">
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="Reason for rejecting (required)"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200" />
                  <div className="flex gap-2">
                    <button disabled={busy || reason.trim().length < 3}
                      onClick={async () => { setError(null); try { await reject({ refereeId: r.id, remarks: reason }).unwrap(); setRejectFor(null); } catch (e: any) { setError(describeApiError(e, "Couldn't submit your response.")); } }}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60">Confirm reject</button>
                    <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-lg border text-sm text-gray-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
